import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Lock } from "lucide-react";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const { clearUsingDefault } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) { setError("Nieuw wachtwoord moet minimaal 8 tekens zijn"); return; }
    if (next !== confirm) { setError("De nieuwe wachtwoorden komen niet overeen"); return; }
    if (next === current) { setError("Nieuw wachtwoord moet verschillen van het huidige"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Wachtwoord gewijzigd");
      clearUsingDefault();
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Wijzigen mislukt");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-3 py-2.5 rounded-md border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm text-zinc-900";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md max-w-[calc(100vw-1.5rem)] max-h-[90vh] overflow-y-auto" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Lock className="h-4 w-4 text-blue-600" /> Wachtwoord wijzigen</DialogTitle>
          <DialogDescription>Kies een nieuw wachtwoord van minimaal 8 tekens.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3.5 py-1">
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Huidig wachtwoord</label>
            <input data-testid="cp-current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className={inputCls} autoComplete="current-password" />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Nieuw wachtwoord</label>
            <input data-testid="cp-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} className={inputCls} autoComplete="new-password" />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Bevestig nieuw wachtwoord</label>
            <input data-testid="cp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} autoComplete="new-password" />
          </div>
          {error && <div data-testid="cp-error" className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700 font-medium">{error}</div>}
          <DialogFooter className="pt-1">
            <button type="button" onClick={() => onOpenChange(false)} className="w-full sm:w-auto px-4 py-2.5 rounded-md text-sm font-medium text-zinc-500 hover:bg-zinc-100">Annuleren</button>
            <button data-testid="cp-submit" type="submit" disabled={saving} className="w-full sm:w-auto px-4 py-2.5 rounded-md text-sm font-semibold bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2">
              {saving ? "Bezig…" : "Wijzigen"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
